import { useState, useEffect, useCallback, useRef } from 'react'
import { addCardCapped, removeOneCard, type LocalDeckCard } from '@/hooks/decks/deckBuilderCards'
import type { Card, DeckResponse } from '@/types'

/**
 * Local (unsaved) deck editing state: metadata, the card list, and the last
 * server snapshot of the cards. Seeds itself from the loaded deck once.
 */
export function useDeckDraft(deckData: DeckResponse | undefined) {
  // Deck metadata
  const [deckName, setDeckNameState] = useState('')
  const [deckDescription, setDeckDescription] = useState('')
  const [selectedLeaderId, setSelectedLeaderId] = useState<number | null>(null)
  const [isPublic, setIsPublic] = useState(false)

  // Deck cards (local until save)
  const [localCards, setLocalCards] = useState<LocalDeckCard[]>([])
  const [serverCardsSnapshot, setServerCardsSnapshot] = useState<LocalDeckCard[]>([])
  const [errors, setErrors] = useState<{ name?: string }>({})

  // Clear the name error as the user types (derived in the handler, not an
  // effect, to avoid the extra render).
  const setDeckName = useCallback((value: string) => {
    setDeckNameState(value)
    setErrors((prev) => (prev.name && value.trim() ? { ...prev, name: undefined } : prev))
  }, [])

  // Initialize editing state from server data ONCE per deck id. Re-running on
  // every `deckData` change (the "make public" toggle's setQueryData, a
  // background refetch, the per-card invalidations during save) would overwrite
  // unsaved local card edits — a silent data-loss bug. After the first load,
  // server state is reconciled by handleSave/handleTogglePublic themselves
  // (which update serverCardsSnapshot / isPublic locally), not by re-seeding.
  const initializedDeckId = useRef<number | null>(null)
  useEffect(() => {
    const deck = deckData?.deck
    if (!deck || initializedDeckId.current === deck.id) return
    initializedDeckId.current = deck.id

    setDeckNameState(deck.name)
    setDeckDescription(deck.description || '')
    setSelectedLeaderId(deck.leader.id)
    setIsPublic(deck.isPublic)
    const cards = deck.cards.map((dc) => ({
      card: dc.card,
      quantity: dc.quantity,
    }))
    setLocalCards(cards)
    setServerCardsSnapshot(cards)
  }, [deckData])

  const handleAddCard = useCallback((card: Card) => setLocalCards((prev) => addCardCapped(prev, card)), [])
  const handleRemoveCard = useCallback((card: Card) => setLocalCards((prev) => removeOneCard(prev, card)), [])

  return {
    deckName,
    setDeckName,
    deckDescription,
    setDeckDescription,
    selectedLeaderId,
    setSelectedLeaderId,
    isPublic,
    setIsPublic,
    localCards,
    serverCardsSnapshot,
    setServerCardsSnapshot,
    errors,
    setErrors,
    handleAddCard,
    handleRemoveCard,
  }
}
